import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Department, DepartmentStatus } from '../../types';
import { departmentsService, CreateDepartmentDto, UpdateDepartmentDto } from '../../services/api/departments';
import Modal from '../../components/common/Modal';
import { useToast } from '../../contexts/ToastContext';

interface DepartmentFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  department: Department | null;
  departments: Department[];
}

interface FormData {
  code: string;
  name: string;
  parentId: string;
  status: DepartmentStatus;
}

interface FormErrors {
  code?: string;
  name?: string;
  parentId?: string;
}

const DepartmentFormModal: React.FC<DepartmentFormModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  department,
  departments,
}) => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useToast();
  const isEditing = !!department;

  const [formData, setFormData] = useState<FormData>({
    code: '',
    name: '',
    parentId: '',
    status: DepartmentStatus.ACTIVE,
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);

  // Reset form when modal opens or department changes
  useEffect(() => {
    if (isOpen) {
      if (department) {
        setFormData({
          code: department.code,
          name: department.name,
          parentId: department.parentId?.toString() || '',
          status: department.status,
        });
      } else {
        setFormData({
          code: '',
          name: '',
          parentId: '',
          status: DepartmentStatus.ACTIVE,
        });
      }
      setErrors({});
    }
  }, [isOpen, department]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when field is edited
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.code.trim()) {
      newErrors.code = t('departments.validation.codeRequired', 'Department code is required');
    } else if (formData.code.length > 50) {
      newErrors.code = t('departments.validation.codeMaxLength', 'Department code must be 50 characters or less');
    } else if (!/^[A-Z0-9_-]+$/i.test(formData.code)) {
      newErrors.code = t('departments.validation.codeFormat', 'Department code can only contain letters, numbers, underscores, and hyphens');
    }

    if (!formData.name.trim()) {
      newErrors.name = t('departments.validation.nameRequired', 'Department name is required');
    } else if (formData.name.length > 255) {
      newErrors.name = t('departments.validation.nameMaxLength', 'Department name must be 255 characters or less');
    }

    // Check for circular reference when editing
    if (isEditing && formData.parentId) {
      const parentId = Number(formData.parentId);
      if (parentId === department!.id) {
        newErrors.parentId = t('departments.validation.circularReference', 'Department cannot be its own parent');
      }
      // Check if selected parent is a child of current department
      const isDescendant = checkIsDescendant(department!.id, parentId);
      if (isDescendant) {
        newErrors.parentId = t('departments.validation.circularReferenceChild', 'Cannot select a child department as parent');
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Helper to check if targetId is a descendant of sourceId
  const checkIsDescendant = (sourceId: number, targetId: number): boolean => {
    const findChildren = (parentId: number): number[] => {
      return departments
        .filter((d) => d.parentId === parentId)
        .flatMap((d) => [d.id, ...findChildren(d.id)]);
    };
    const descendants = findChildren(sourceId);
    return descendants.includes(targetId);
  };

  // Get available parent options (exclude current department and its descendants when editing)
  const getParentOptions = (): Department[] => {
    if (!isEditing) {
      return departments.filter((d) => d.status === DepartmentStatus.ACTIVE);
    }

    const descendantIds = new Set<number>();
    const findDescendants = (parentId: number) => {
      departments
        .filter((d) => d.parentId === parentId)
        .forEach((d) => {
          descendantIds.add(d.id);
          findDescendants(d.id);
        });
    };
    findDescendants(department!.id);

    return departments.filter(
      (d) =>
        d.id !== department!.id &&
        !descendantIds.has(d.id) &&
        d.status === DepartmentStatus.ACTIVE
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) return;

    setSubmitting(true);
    try {
      if (isEditing) {
        const updateData: UpdateDepartmentDto = {
          name: formData.name.trim(),
          parentId: formData.parentId ? Number(formData.parentId) : null,
          status: formData.status,
        };
        await departmentsService.update(department!.id, updateData);
        showSuccess(t('departments.updateSuccess', 'Department updated successfully'));
      } else {
        const createData: CreateDepartmentDto = {
          code: formData.code.trim().toUpperCase(),
          name: formData.name.trim(),
          parentId: formData.parentId ? Number(formData.parentId) : null,
          status: formData.status,
        };
        await departmentsService.create(createData);
        showSuccess(t('departments.createSuccess', 'Department created successfully'));
      }
      onSuccess();
      onClose();
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.message || err.message || 'Operation failed';
      showError(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const parentOptions = getParentOptions();

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEditing ? t('departments.edit') : t('departments.create')}
    >
      <form onSubmit={handleSubmit} className="department-form">
        <div className="form-group">
          <label htmlFor="code">
            {t('departments.code')} <span className="required">*</span>
          </label>
          <input
            type="text"
            id="code"
            name="code"
            value={formData.code}
            onChange={handleChange}
            className={errors.code ? 'error' : ''}
            disabled={isEditing}
            placeholder="e.g., IT, HR, SALES"
            maxLength={50}
          />
          {errors.code && <span className="form-error">{errors.code}</span>}
          {isEditing && (
            <span className="form-hint">
              {t('departments.codeCannotChange', 'Department code cannot be changed after creation')}
            </span>
          )}
        </div>

        <div className="form-group">
          <label htmlFor="name">
            {t('departments.name')} <span className="required">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={errors.name ? 'error' : ''}
            placeholder={t('departments.namePlaceholder', 'Enter department name')}
            maxLength={255}
          />
          {errors.name && <span className="form-error">{errors.name}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="parentId">{t('departments.parent')}</label>
          <select
            id="parentId"
            name="parentId"
            value={formData.parentId}
            onChange={handleChange}
            className={errors.parentId ? 'error' : ''}
          >
            <option value="">{t('departments.noParent', '-- No Parent (Root Department) --')}</option>
            {parentOptions.map((dept) => (
              <option key={dept.id} value={dept.id}>
                {dept.code} - {dept.name}
              </option>
            ))}
          </select>
          {errors.parentId && <span className="form-error">{errors.parentId}</span>}
        </div>

        <div className="form-group">
          <label htmlFor="status">{t('departments.status')}</label>
          <select
            id="status"
            name="status"
            value={formData.status}
            onChange={handleChange}
          >
            <option value={DepartmentStatus.ACTIVE}>{t('status.active')}</option>
            <option value={DepartmentStatus.INACTIVE}>{t('status.inactive')}</option>
          </select>
        </div>

        <div className="form-actions">
          <button
            type="button"
            className="button button-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            className="button button-primary"
            disabled={submitting}
          >
            {submitting
              ? t('common.loading')
              : isEditing
              ? t('common.save')
              : t('common.create')}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default DepartmentFormModal;
